import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox,
  Layout, BlockStack, InlineStack, Box, Spinner,
} from '@shopify/polaris';
import { useUrgencyScarcity } from '../../hooks/useUrgencyScarcity';
import type { UrgencyScarcitySettings } from '../../api/urgencyScarcity';

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function unitsFor(ms: number): { value: number; label: string }[] {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const units = [];
  if (days > 0) units.push({ value: days, label: 'days' });
  units.push({ value: hours, label: 'hrs' });
  units.push({ value: minutes, label: 'min' });
  units.push({ value: seconds, label: 'sec' });
  return units;
}

const pad = (n: number) => String(n).padStart(2, '0');

function LivePreview({ settings }: { settings: UrgencyScarcitySettings }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!settings.countdown_enabled || !settings.countdown_end_at) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [settings.countdown_enabled, settings.countdown_end_at]);

  if (!settings.low_stock_enabled && !settings.countdown_enabled) {
    return <Text as="p" tone="subdued">Enable the low-stock badge or sale countdown above to preview them.</Text>;
  }

  const endAt = settings.countdown_end_at ? new Date(settings.countdown_end_at).getTime() : NaN;
  const remaining = endAt - now;
  const expired = !Number.isNaN(endAt) && remaining <= 0;
  const badgeMessage = settings.low_stock_message.replace('{quantity}', String(settings.low_stock_threshold));

  return (
    <BlockStack gap="400">
      {settings.low_stock_enabled && (
        <Box>
          <Text as="p" variant="bodySm" tone="subdued">On a product page:</Text>
          <Box paddingBlockStart="150">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 999, background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', color: '#92400E', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(217,119,6,0.18)' }}>
              <span>🔥</span>
              {badgeMessage}
            </span>
          </Box>
        </Box>
      )}

      {settings.countdown_enabled && (
        <Box>
          <Text as="p" variant="bodySm" tone="subdued">Sitewide bar:</Text>
          <Box paddingBlockStart="150">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', width: '100%', padding: '11px 16px', borderRadius: 6, background: 'linear-gradient(90deg, #991B1B, #B91C1C 50%, #991B1B)', color: '#fff', fontSize: 14, fontWeight: 700, textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
              {expired ? (
                <span>{settings.countdown_expired_message}</span>
              ) : (
                <>
                  <span>⏰ {settings.countdown_message}</span>
                  {Number.isNaN(endAt) ? (
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>--:--:--</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {unitsFor(remaining).map((u, i) => (
                        <span key={u.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {i > 0 && <span style={{ opacity: 0.6, fontWeight: 700 }}>:</span>}
                          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 34, padding: '2px 6px', borderRadius: 6, background: 'rgba(255,255,255,0.16)' }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: 14, letterSpacing: '0.02em', lineHeight: 1.2 }}>{pad(u.value)}</span>
                            <span style={{ fontSize: 8.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.85, lineHeight: 1.2 }}>{u.label}</span>
                          </span>
                        </span>
                      ))}
                    </span>
                  )}
                </>
              )}
            </div>
          </Box>
        </Box>
      )}
    </BlockStack>
  );
}

export function UrgencyScarcityPanel() {
  const { settings, loading, saving, error, fetchSettings, save, setError } = useUrgencyScarcity();

  const [form, setForm] = useState<UrgencyScarcitySettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Urgency & Scarcity Bar">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Urgency & Scarcity Bar">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load urgency & scarcity bar settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Urgency & Scarcity Bar"
      subtitle="Real low-stock messaging on product pages, plus an optional sitewide sale countdown"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. Changes appear on your storefront within about a minute.</p>
              </Banner>
            )}

            <Card>
              <BlockStack gap="300">
                <Checkbox
                  label="Enable urgency & scarcity messaging"
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Low-Stock Badge</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Shows the real, live inventory count on product pages — read directly from your theme, never cached or stale.
                </Text>

                <Checkbox
                  label="Show a low-stock badge on product pages"
                  checked={form.low_stock_enabled}
                  onChange={(v) => setForm({ ...form, low_stock_enabled: v })}
                />

                <InlineStack gap="300">
                  <Box minWidth="160px">
                    <TextField
                      label="Show when stock is at or below"
                      type="number"
                      autoComplete="off"
                      value={String(form.low_stock_threshold)}
                      onChange={(v) => setForm({ ...form, low_stock_threshold: Number(v) || 1 })}
                    />
                  </Box>
                </InlineStack>

                <TextField
                  label="Message"
                  helpText="Use {quantity} where the live stock count should appear"
                  autoComplete="off"
                  value={form.low_stock_message}
                  onChange={(v) => setForm({ ...form, low_stock_message: v })}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Sale Countdown Bar</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  A sitewide bar counting down to a sale deadline you set.
                </Text>

                <Checkbox
                  label="Show a sitewide sale countdown"
                  checked={form.countdown_enabled}
                  onChange={(v) => setForm({ ...form, countdown_enabled: v })}
                />

                <TextField
                  label="Ends at"
                  type="datetime-local"
                  autoComplete="off"
                  value={toDatetimeLocal(form.countdown_end_at)}
                  onChange={(v) => setForm({ ...form, countdown_end_at: v ? new Date(v).toISOString() : null })}
                />
                <TextField label="Message" autoComplete="off" value={form.countdown_message} onChange={(v) => setForm({ ...form, countdown_message: v })} />
                <TextField
                  label="Message after it ends"
                  autoComplete="off"
                  value={form.countdown_expired_message}
                  onChange={(v) => setForm({ ...form, countdown_expired_message: v })}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Live Preview</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This is exactly what shoppers see on your storefront.
                </Text>
                <LivePreview settings={form} />
              </BlockStack>
            </Card>

            <Box>
              <Button variant="primary" loading={saving} onClick={handleSave}>Save Settings</Button>
            </Box>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
                <Text as="p" tone="subdued">
                  The low-stock badge is added per product page via <strong>Online Store &rarr; Themes &rarr;
                  Customize &rarr; Add block &rarr; Apps &rarr; Low-Stock Badge</strong>. The sitewide countdown bar
                  is a one-time toggle under <strong>App embeds &rarr; Sale Countdown Bar</strong>.
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
